import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import { Link } from 'components/Link'
import { Dropdown } from '../../../../components/Dropdown'
import { CellWithSubtitle } from 'components/CellWithSubtitle/CellWithSubtitle'
import { Sorter } from 'components/Sorter/Sorter'
import { formatAddress } from '../../../../helper/common'
import { routes } from 'router'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import { DescriptionCell } from '../../../../components/DescriptionCell/DescriptionCell'
import Typography from '../../../../components/Typography/Typography'
import { TYPOGRAPHY_WEIGHT } from '../../../../components/Typography'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import {
  VENDOR_ACCESS_OPTIONS,
  VENDOR_USER_STATUS_OPTIONS
} from '../VendorsList/constants'
import { TVendorsListFiltersFunc } from '../VendorsList/types'
import { TMyVendorColumnsFunc, TMyVendors, TMyVendorsFilters } from './types'

import {
  MY_VENDOR_USER_STATUS_NAME,
  MY_VENDOR_USER_STATUS_TYPES_TO_CLASSNAME,
  Status
} from '../../../../components/Status'

export const MY_VENDOR_USER_STATUSES_OPTIONS = [
  {
    label: 'Active',
    value: 'active'
  },
  {
    label: 'Pending',
    value: 'pending'
  },
  {
    label: 'Inactive',
    value: 'inactive'
  }
]

export const columns = ({
  applyFilters,
  filters
}: TMyVendorColumnsFunc): ColumnsType<TMyVendors> => {
  return [
    {
      dataIndex: 'name',
      key: 'name',
      width: 350,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TMyVendors>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Vendor name
        </Sorter>
      ),
      render: (_, { name, uuid, business_name }) => (
        <Tooltip title={name} placement="topLeft">
          <div>
            <CellWithSubtitle
              className="vendor-repository-name-col"
              title={
                <Link to={`${routes.myCommunity}/vendor_details/${uuid}`}>
                  {business_name}
                </Link>
              }
            />
          </div>
        </Tooltip>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'participation_status',
      key: 'participation_status',
      width: 208,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TMyVendors>
          dataIndex="participation_status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Participation status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<TMyVendorsFilters, TVendorsListFiltersFunc, unknown>
          items={MY_VENDOR_USER_STATUSES_OPTIONS}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="participation_status"
          {...props}
        />
      ),
      render: (_, { participation_status }) => (
        <Status.Default
          className={
            MY_VENDOR_USER_STATUS_TYPES_TO_CLASSNAME[participation_status]
          }
        >
          {MY_VENDOR_USER_STATUS_NAME[participation_status]}
        </Status.Default>
      ),
      filterIcon: !!filters?.participation_status?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'alternate_name_set',
      key: 'alternate_name_set',
      width: 220,
      title: 'Vendor alternate names',
      render: (_, { alternate_name_set }) => {
        const text = alternate_name_set.map((i) => i).join(', ')
        return (
          <DescriptionCell
            title={'Vendor alternate names'}
            content={text}
            text={text}
          />
        )
      },
      showSorterTooltip: false
    },
    {
      dataIndex: 'address',
      key: 'address',
      width: 160,
      title: 'Addresses',
      render: (
        _,
        { address, remit_to_address_set, order_from_address_set }
      ) => {
        return (
          <DescriptionCell
            title={'Addresses'}
            content={
              <div className="column gap-16">
                <div className="column">
                  <Typography.Body2 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                    Legal address
                  </Typography.Body2>
                  <Typography.Body1>{formatAddress(address)}</Typography.Body1>
                </div>
                {!!remit_to_address_set.length && (
                  <div className="column">
                    <div className="separator mb-16" />
                    <Typography.Body2 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                      Remit To Addresses
                    </Typography.Body2>
                    {remit_to_address_set.map((item) => {
                      const address = formatAddress(item)
                      return (
                        <Typography.Body1 key={address}>
                          {address}
                        </Typography.Body1>
                      )
                    })}
                  </div>
                )}
                {!!order_from_address_set?.length && (
                  <div className="column">
                    <div className="separator mb-16" />
                    <Typography.Body2 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                      Order From Addresses
                    </Typography.Body2>

                    {order_from_address_set.map((item) => {
                      const address = formatAddress(item)
                      return (
                        <Typography.Body1 key={address}>
                          {address}
                        </Typography.Body1>
                      )
                    })}
                  </div>
                )}
              </div>
            }
            triggerElement={
              <Typography.Body1 className={'main-color-text cursor-pointer'}>
                See addresses
              </Typography.Body1>
            }
          />
        )
      },
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'vendor_permission',
      key: 'vendor_permission',
      width: 160,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TMyVendors>
          dataIndex="vendor_permission"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Access type
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<TMyVendorsFilters, TVendorsListFiltersFunc, unknown>
          items={VENDOR_ACCESS_OPTIONS}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="vendor_permission"
          {...props}
        />
      ),
      render: (_, { vendor_permission }) =>
        VENDOR_ACCESS_OPTIONS.find((i) => i.value === vendor_permission)?.label,
      filterIcon: !!filters?.vendor_permission?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'user_status',
      key: 'user_status',
      width: 160,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TMyVendors>
          dataIndex="user_status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Vendor users
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<TMyVendorsFilters, TVendorsListFiltersFunc, unknown>
          items={VENDOR_USER_STATUS_OPTIONS}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="user_status"
          {...props}
        />
      ),
      render: (_, { user_status }) =>
        VENDOR_USER_STATUS_OPTIONS.find((i) => i.value === user_status)?.label,
      filterIcon: filters?.user_status?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    }
  ]
}
